// Lightweight per-step required-field validator.
// Returns { ok, missing: [{ key, label, testid }] }.
// Augmented with Wisconsin DHS F-11133 compliance rules (see lib/dhsRules.js).

import { adlDhsIssues, nursingDhsIssues, dateDhsIssues } from "./dhsRules";

function need(val, label, testid, key) {
  const v = typeof val === "string" ? val.trim() : val;
  if (v === undefined || v === null || v === "" || (Array.isArray(v) && v.length === 0)) {
    return { key, label, testid };
  }
  return null;
}

// Convert a DHS issue to the {key,label,testid} shape this module already uses.
// Only "error" severities block Next; warnings/info surface in the AI review panel.
function dhsToMissing(issues) {
  return issues
    .filter((i) => i.severity === "error")
    .map((i) => ({ key: i.field, label: i.label, testid: i.testid }));
}

export function validateStep(step, data) {
  const missing = [];
  switch (step) {
    case 1: {
      const a = data.agency || {}, c = data.client || {}, p = data.physician || {};
      [
        need(a.name, "Agency Name", "agency-name", "agency.name"),
        need(a.provider_number, "Provider Number", "agency-provider-number", "agency.provider_number"),
        need(a.phone, "Agency Phone", "agency-phone", "agency.phone"),
        need(c.first_name, "Client First Name", "client-first", "client.first_name"),
        need(c.last_name, "Client Last Name", "client-last", "client.last_name"),
        need(c.date_of_birth, "Date of Birth", "client-dob", "client.date_of_birth"),
        need(c.forwardhealth_member_id, "ForwardHealth Member ID", "client-member-id", "client.forwardhealth_member_id"),
        need(p.name, "Physician Name", "phys-name", "physician.name"),
        need(p.npi, "Physician NPI", "phys-npi", "physician.npi"),
      ].forEach((x) => x && missing.push(x));
      // DHS: screen completion date vs SOC (cross-step, but Step 1 owns the date)
      dhsToMissing(dateDhsIssues(data)).forEach((m) => missing.push(m));
      break;
    }
    case 2: {
      // At least one ADL scored
      const hasAny = Object.values(data.adl || {}).some(
        (r) => (r?.pcst_level || r?.score || "").toString().trim() !== ""
      );
      if (!hasAny) missing.push({ key: "adl", label: "Score at least one ADL (try the Quick-set panel)", testid: "adl-row-bathing" });
      // DHS-C-COMMENT + DHS-AGE-GATE + DHS-TUBE-LINK + DHS-LIFT-LINK
      dhsToMissing(adlDhsIssues(data)).forEach((m) => missing.push(m));
      break;
    }
    case 3: {
      // DHS-PRN-FORMAT / DHS-SEIZURE / DHS-BEHAVIOR (errors only)
      dhsToMissing(nursingDhsIssues(data)).forEach((m) => missing.push(m));
      break;
    }
    case 5: {
      const dx = (data.diagnoses || []).filter((d) => (d.icd10 || d.description || "").trim());
      if (!dx.length) missing.push({ key: "diagnoses", label: "At least one diagnosis", testid: "dx-autocomplete" });
      const meds = (data.medications || []).filter((m) => (m.name || "").trim());
      if (!meds.length) missing.push({ key: "medications", label: "At least one medication (or confirm none)", testid: "med-autocomplete" });
      break;
    }
    case 8: {
      const c = data.cms485 || {};
      [
        need(c.cert_period_from, "Cert Period Start", "cms-from", "cms485.cert_period_from"),
        need(c.cert_period_to, "Cert Period End", "cms-to", "cms485.cert_period_to"),
      ].forEach((x) => x && missing.push(x));
      break;
    }
    default:
      break;
  }
  return { ok: missing.length === 0, missing };
}

export function focusMissing(testid) {
  if (!testid) return;
  const el = document.querySelector(`[data-testid="${testid}"]`);
  if (!el) return;
  try { el.scrollIntoView({ behavior: "smooth", block: "center" }); } catch { /* noop */ }
  el.classList.add("ring-2","ring-[#c05746]","ring-offset-2");
  setTimeout(() => el.classList.remove("ring-2","ring-[#c05746]","ring-offset-2"), 2400);
  try { el.focus(); } catch { /* noop */ }
}
